/**
 * Identity commands — sherwood identity <subcommand>
 *
 * Wraps the Agent0 SDK (@agent0-sdk) for ERC-8004 agent identity management.
 * Handles: mint (register), set metadata, check status, load existing agent.
 * Required before creating or joining funds.
 */

import { Command } from "commander";
import type { Address } from "viem";
import chalk from "chalk";
import ora from "ora";
import { SDK } from "agent0-sdk";
import { getPublicClient, getAccount, formatContractError } from "../lib/client.js";
import { getExplorerUrl, getChain, getChainConfig, getRpcUrl } from "../lib/network.js";
import { AGENT_REGISTRY } from "../lib/addresses.js";
import { setAgentId, getAgentId, loadConfig } from "../lib/config.js";
import { isCalldataOnly, emitCalldata } from "../lib/calldata.js";
import { encodeIdentityMint, type ChainId } from "@sherwoodagent/sdk";

// ── ABI (minimal, for status reads without SDK) ──

const IDENTITY_REGISTRY_ABI = [
  {
    name: "balanceOf",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "owner", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "ownerOf",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "tokenId", type: "uint256" }],
    outputs: [{ name: "", type: "address" }],
  },
] as const;

/**
 * Initialize the Agent0 SDK with the current network config.
 */
function getAgent0SDK(): SDK {
  // Read key from config first, env var as fallback
  const config = loadConfig();
  const key = config.privateKey || process.env.PRIVATE_KEY;
  if (!key) {
    throw new Error(
      "Private key not found. Run 'sherwood config set --private-key <key>' or set PRIVATE_KEY env var.",
    );
  }

  const chain = getChain();
  const sdkConfig: ConstructorParameters<typeof SDK>[0] = {
    chainId: chain.id,
    rpcUrl: getRpcUrl(),
    privateKey: key.startsWith("0x") ? key : `0x${key}`,
  };

  // Custom-chainId vnet (SIM_CHAIN_ID override active — getChain().id differs
  // from the registry's canonical id): the Agent0 SDK resolves the ERC-8004
  // registries by chain id and doesn't know the fork's id, so hand it the
  // CLI's own registry addresses (the forked Base state contains them).
  if (chain.id !== getChainConfig().chain.id) {
    sdkConfig.registryOverrides = {
      [chain.id]: {
        IDENTITY: AGENT_REGISTRY().IDENTITY_REGISTRY,
        REPUTATION: AGENT_REGISTRY().REPUTATION_REGISTRY,
      },
    };
  }

  return new SDK(sdkConfig);
}

export function registerIdentityCommands(program: Command): void {
  const identity = program.command("identity").description("Manage ERC-8004 agent identity (via Agent0 SDK)");

  // ── identity mint ──

  identity
    .command("mint")
    .description("Register a new ERC-8004 agent identity (required before creating/joining funds)")
    .requiredOption("--name <name>", "Agent name (e.g. 'Alpha Seeker Agent')")
    .option("--description <desc>", "Agent description", "Sherwood fund agent")
    .option("--image <uri>", "Agent image URI (IPFS recommended)")
    .action(async (opts) => {
      // --calldata-only: emit the unsigned ERC-8004 register() calldata and
      // stop. No Agent0 SDK init, no IPFS, no private key.
      if (isCalldataOnly()) {
        emitCalldata(
          encodeIdentityMint(
            {
              name: opts.name,
              description: opts.description,
              image: opts.image,
            },
            getChain().id as ChainId,
          ),
        );
        return;
      }

      const account = getAccount();

      // Check if wallet already has an identity
      const existingId = getAgentId();
      if (existingId) {
        console.log(chalk.yellow(`You already have an agent identity saved: #${existingId}`));
        console.log(chalk.dim("  Minting a new one anyway. The old ID is not affected."));
        console.log();
      }

      const spinner = ora("Initializing Agent0 SDK...").start();
      try {
        const sdk = getAgent0SDK();

        // Create agent with metadata
        spinner.text = "Creating agent profile...";
        const agent = sdk.createAgent(opts.name, opts.description, opts.image);

        // Register on-chain (mints ERC-8004 NFT)
        spinner.text = "Registering on-chain (minting ERC-8004 identity)...";
        const txHandle = await agent.registerOnChain();

        spinner.text = "Waiting for confirmation...";
        await txHandle.waitMined();

        const agentId = agent.agentId;
        if (!agentId) {
          spinner.warn("Identity registered but could not read agentId");
          console.log(chalk.dim("  Check the transaction on the explorer."));
          return;
        }

        // Agent0 agentId format is "chainId:tokenId" — extract the token ID
        const tokenId = Number(agentId.includes(":") ? agentId.split(":")[1] : agentId);

        // Save to config
        setAgentId(tokenId);

        spinner.succeed(`Agent identity registered: #${tokenId}`);
        console.log(chalk.dim(`  Agent0 ID: ${agentId}`));
        console.log(chalk.dim(`  Name:      ${opts.name}`));
        console.log(chalk.dim(`  Owner:     ${account.address}`));
        console.log(chalk.dim(`  Saved to   ~/.sherwood/config.json`));
        console.log();
        console.log(chalk.green("You can now create funds:"));
        console.log(chalk.dim(`  sherwood fund create --agent-id ${tokenId} --subdomain <name> --name <name>`));
      } catch (err) {
        spinner.fail("Failed to register identity");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });

  // ── identity load ──

  identity
    .command("load")
    .description("Load an existing ERC-8004 agent identity into your config")
    .requiredOption("--id <tokenId>", "Agent token ID to load")
    .action(async (opts) => {
      const account = getAccount();
      const client = getPublicClient();
      const registry = AGENT_REGISTRY().IDENTITY_REGISTRY;
      const tokenId = Number(opts.id);

      const spinner = ora(`Verifying ownership of agent #${tokenId}...`).start();
      try {
        const owner = await client.readContract({
          address: registry,
          abi: IDENTITY_REGISTRY_ABI,
          functionName: "ownerOf",
          args: [BigInt(tokenId)],
        }) as Address;

        if (owner.toLowerCase() !== account.address.toLowerCase()) {
          spinner.fail(`Agent #${tokenId} is owned by ${owner}, not your wallet`);
          process.exit(1);
        }

        setAgentId(tokenId);
        spinner.succeed(`Agent #${tokenId} loaded and saved to config`);
        console.log(chalk.dim(`  Owner:  ${account.address}`));
        console.log(chalk.dim(`  Saved to ~/.sherwood/config.json`));
      } catch (err) {
        spinner.fail("Failed to load identity");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });

  // ── identity find ──

  identity
    .command("find")
    .description("Search ERC-8004 agents by wallet address or name (recover a lost agent ID)")
    .option("--wallet <address>", "Filter by owner / wallet address")
    .option("--name <text>", "Filter by agent name (exact or substring match, depending on registry)")
    .option("--all-chains", "Search across all chains, not just the active one")
    .action(async (opts) => {
      if (!opts.wallet && !opts.name) {
        console.error(chalk.red("Pass --wallet <address> or --name <text> (or both)."));
        process.exit(1);
      }

      const spinner = ora("Searching ERC-8004 registry...").start();
      try {
        const sdk = getAgent0SDK();
        const filters: Record<string, unknown> = {};
        if (opts.wallet) filters.walletAddress = opts.wallet;
        if (opts.name) filters.name = opts.name;
        // SearchFilters['chains']: explicit list per-chain or 'all'
        filters.chains = opts.allChains ? "all" : [getChain().id];

        const results = await sdk.searchAgents(filters);
        spinner.stop();

        if (results.length === 0) {
          console.log(chalk.dim("No agents found matching those filters."));
          console.log(chalk.dim("If you minted on a different chain, retry with --all-chains."));
          return;
        }

        console.log();
        console.log(chalk.bold(`Found ${results.length} agent${results.length === 1 ? "" : "s"}`));
        console.log(chalk.dim("─".repeat(50)));
        for (const a of results) {
          // Agent0 agentId format is "chainId:tokenId" — split for display
          const idStr = String(a.agentId);
          const tokenId = idStr.includes(":") ? idStr.split(":")[1] : idStr;
          console.log(`  ${chalk.bold(`#${tokenId}`)}  ${a.name || chalk.dim("(no name)")}`);
          console.log(chalk.dim(`    Chain:  ${a.chainId}`));
          console.log(chalk.dim(`    Full:   ${idStr}`));
          if (a.owners?.length) {
            console.log(chalk.dim(`    Owner:  ${a.owners[0]}`));
          }
          if (a.walletAddress) {
            console.log(chalk.dim(`    Wallet: ${a.walletAddress}`));
          }
        }
        console.log();
        console.log(chalk.green("To bind one of these to this machine:"));
        console.log(chalk.dim(`  sherwood identity load --id <tokenId>`));
      } catch (err) {
        spinner.fail("Search failed");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });

  // ── identity status ──

  identity
    .command("status")
    .description("Show your agent identity status")
    .action(async () => {
      const account = getAccount();
      const registry = AGENT_REGISTRY().IDENTITY_REGISTRY;
      const client = getPublicClient();

      const spinner = ora("Checking identity...").start();
      try {
        const balance = await client.readContract({
          address: registry,
          abi: IDENTITY_REGISTRY_ABI,
          functionName: "balanceOf",
          args: [account.address],
        }) as bigint;

        spinner.stop();

        const savedId = getAgentId();

        console.log();
        console.log(chalk.bold("Agent Identity (ERC-8004)"));
        console.log(chalk.dim("─".repeat(40)));
        console.log(`  Wallet:     ${account.address}`);
        console.log(`  Registry:   ${registry}`);
        console.log(`  NFTs owned: ${balance.toString()}`);

        if (savedId) {
          // Verify the saved ID is still owned by this wallet
          try {
            const owner = await client.readContract({
              address: registry,
              abi: IDENTITY_REGISTRY_ABI,
              functionName: "ownerOf",
              args: [BigInt(savedId)],
            }) as Address;

            const isOwner = owner.toLowerCase() === account.address.toLowerCase();
            console.log(`  Saved ID:   #${savedId} ${isOwner ? chalk.green("(verified)") : chalk.red("(owned by " + owner + ")")}`);

            // Load full agent details via SDK if verified
            if (isOwner) {
              try {
                const sdk = getAgent0SDK();
                const agent = await sdk.loadAgent(`${getChain().id}:${savedId}`);
                if (agent.name) console.log(`  Name:       ${agent.name}`);
                if (agent.description) console.log(`  Desc:       ${chalk.dim(agent.description)}`);
                if (agent.walletAddress) console.log(`  Wallet:     ${agent.walletAddress}`);
              } catch {
                // SDK load failed — not critical, basic info already shown
              }
            }
          } catch {
            console.log(`  Saved ID:   #${savedId} ${chalk.red("(token not found)")}`);
          }
          console.log();
        } else if (balance > 0n) {
          // Wallet owns identities but none saved locally — fetch and surface
          // them so the user can pick one to bind. Closes the trap where a
          // fresh CLI install on a wallet with prior identities reports
          // "Saved ID: none" without explaining what's actually on-chain.
          console.log(`  Saved ID:   ${chalk.yellow("none (but this wallet owns NFTs — listing below)")}`);
          console.log();

          const lookupSpinner = ora("Resolving owned token IDs via Agent0 SDK...").start();
          try {
            const sdk = getAgent0SDK();
            const results = await sdk.searchAgents({
              walletAddress: account.address,
              chains: [getChain().id],
            });
            lookupSpinner.stop();

            if (results.length === 0) {
              console.log(chalk.dim("  No agents resolved for this wallet on the active chain."));
              console.log(chalk.dim("  If you minted on a different chain, try:"));
              console.log(chalk.dim("    sherwood identity find --wallet " + account.address + " --all-chains"));
            } else {
              console.log(chalk.bold(`  Owned identities on this chain (${results.length}):`));
              for (const a of results) {
                const idStr = String(a.agentId);
                const tokenId = idStr.includes(":") ? idStr.split(":")[1] : idStr;
                console.log(`    ${chalk.bold(`#${tokenId}`)}  ${a.name || chalk.dim("(no name)")}`);
              }
              console.log();
              console.log(chalk.green("  Bind one to this machine:"));
              if (results.length === 1) {
                const idStr = String(results[0].agentId);
                const tokenId = idStr.includes(":") ? idStr.split(":")[1] : idStr;
                console.log(chalk.dim(`    sherwood identity load --id ${tokenId}`));
              } else {
                console.log(chalk.dim("    sherwood identity load --id <tokenId>"));
              }
            }
          } catch (err) {
            lookupSpinner.warn("Could not resolve owned token IDs via Agent0 SDK");
            console.log(chalk.dim(`  ${(err as Error).message}`));
            console.log(chalk.dim("  Try: sherwood identity find --wallet " + account.address));
          }
          console.log();
        } else {
          console.log(`  Saved ID:   ${chalk.dim("none — run 'sherwood identity mint --name <name>'")}`);
          console.log();
        }
      } catch (err) {
        spinner.fail("Failed to check identity");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });
}
